import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { apiErrorMessage, integrationsService } from '../../services/api';
import { EmptyState, PageHeader, StatusChip, TableSkeleton } from '../../components/ui';
import { useShop } from '../../contexts/ShopContext';
import { useAuth } from '../../contexts/AuthContext';
import StripeConnectionCard from '../../components/integrations/StripeConnectionCard';
import SupplierConnections from '../../components/integrations/SupplierConnections';
import {
  INTEGRATION_KEYS,
  IntegrationConnection,
  IntegrationEventTypeInfo,
  ShopIntegrationAccess,
  UserRole,
  WebhookDelivery,
  WebhookEndpoint,
} from '../../types';
import { formatTimestamp } from '../../utils/locale';

/**
 * Outbound webhooks and connected providers.
 *
 * Every webhook body is signed (X-Mechanix-Signature, HMAC-SHA256 over
 * `${t}.${body}`) with a secret the shop sees exactly once — on create or
 * rotate. The server encrypts it at rest and never returns it again.
 */

interface EndpointForm {
  url: string;
  description: string;
  events: string[];
  isActive: boolean;
}

const EMPTY_FORM: EndpointForm = {
  url: '',
  description: '',
  events: ['*'],
  isActive: true,
};

const ALL = '*';

const formatWhen = (value: number | string | null | undefined) =>
  value ? formatTimestamp(Number(value)) : '—';

const IntegrationsPage: React.FC = () => {
  const { selectedShop } = useShop();
  const { user } = useAuth();
  // Shop admins are always scoped server-side; only a super admin names a shop.
  const shopId = user?.role === UserRole.SUPER_ADMIN ? selectedShop?.id : undefined;

  const [endpoints, setEndpoints] = useState<WebhookEndpoint[]>([]);
  const [connections, setConnections] = useState<IntegrationConnection[]>([]);
  const [eventTypes, setEventTypes] = useState<IntegrationEventTypeInfo[]>([]);
  const [access, setAccess] = useState<ShopIntegrationAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [editing, setEditing] = useState<WebhookEndpoint | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<EndpointForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<WebhookEndpoint | null>(null);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);

  const [logFor, setLogFor] = useState<WebhookEndpoint | null>(null);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [logLoading, setLogLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [hooks, connected, catalog, allowed] = await Promise.all([
        integrationsService.listWebhooks(shopId),
        integrationsService.listConnections(shopId),
        integrationsService.getEventTypes(),
        // Older servers have no switches — treat everything as allowed.
        integrationsService.getAccess(shopId).catch(() => [] as ShopIntegrationAccess[]),
      ]);
      setAccess(allowed);
      setEndpoints(hooks);
      setConnections(connected);
      setEventTypes(catalog.events.filter((event) => event.type !== 'webhook.ping'));
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load integrations.'));
    } finally {
      setLoading(false);
    }
  }, [shopId]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadDeliveries = useCallback(async (endpoint: WebhookEndpoint) => {
    try {
      setLogLoading(true);
      setDeliveries(await integrationsService.listDeliveries(endpoint.id));
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load the delivery log.'));
    } finally {
      setLogLoading(false);
    }
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (endpoint: WebhookEndpoint) => {
    setEditing(endpoint);
    setForm({
      url: endpoint.url,
      description: endpoint.description ?? '',
      events: endpoint.events,
      isActive: endpoint.isActive,
    });
    setDialogOpen(true);
  };

  const openLog = (endpoint: WebhookEndpoint) => {
    setLogFor(endpoint);
    setDeliveries([]);
    void loadDeliveries(endpoint);
  };

  const toggleEvent = (type: string) =>
    setForm((current) => {
      if (type === ALL) {
        return { ...current, events: current.events.includes(ALL) ? [] : [ALL] };
      }
      const selected = current.events.filter((event) => event !== ALL);
      return {
        ...current,
        events: selected.includes(type)
          ? selected.filter((event) => event !== type)
          : [...selected, type],
      };
    });

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    const payload = {
      url: form.url.trim(),
      description: form.description.trim() || undefined,
      events: form.events,
    };

    try {
      if (editing) {
        await integrationsService.updateWebhook(editing.id, {
          ...payload,
          isActive: form.isActive,
        });
        setNotice('Webhook updated');
      } else {
        const { secret } = await integrationsService.createWebhook(payload, shopId);
        setRevealedSecret(secret);
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save the webhook.'));
    } finally {
      setSaving(false);
    }
  };

  const handleRotate = async (endpoint: WebhookEndpoint) => {
    try {
      const { secret } = await integrationsService.rotateSecret(endpoint.id);
      setRevealedSecret(secret);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not rotate the signing secret.'));
    }
  };

  const handleTest = async (endpoint: WebhookEndpoint) => {
    try {
      await integrationsService.sendTest(endpoint.id);
      setNotice('Test event queued — check the delivery log in a moment');
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not send a test event.'));
    }
  };

  const handleRetry = async (delivery: WebhookDelivery) => {
    try {
      await integrationsService.retryDelivery(delivery.id);
      setNotice('Delivery queued for retry');
      if (logFor) await loadDeliveries(logFor);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not retry the delivery.'));
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await integrationsService.removeWebhook(pendingDelete.id);
      setNotice('Webhook removed');
      await load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not remove the webhook.'));
    } finally {
      setPendingDelete(null);
    }
  };

  const handleDisconnect = async (connection: IntegrationConnection) => {
    try {
      await integrationsService.disconnect(connection.provider, shopId);
      setNotice(`${connection.provider} disconnected`);
      await load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not disconnect the provider.'));
    }
  };

  const copySecret = async () => {
    if (!revealedSecret) return;
    try {
      await navigator.clipboard.writeText(revealedSecret);
      setNotice('Secret copied');
    } catch {
      setNotice('Copy failed — select the secret and copy it by hand');
    }
  };

  const webhooksAccess = access.find((entry) => entry.key === INTEGRATION_KEYS.WEBHOOKS);
  const webhooksAllowed = webhooksAccess?.enabled ?? true;

  const describeEvents = (events: string[]) =>
    events.includes(ALL) ? 'All events' : `${events.length} event${events.length === 1 ? '' : 's'}`;

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <PageHeader
        eyebrow={selectedShop?.name}
        title="Integrations"
        subtitle="Card payments, parts suppliers, and webhooks that send bookings, invoices and payments to other systems as they happen. Every webhook is signed, and failed deliveries retry automatically for about a day and a half."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            disabled={!webhooksAllowed}
          >
            New webhook
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Typography variant="h6" component="h2" fontWeight={700} sx={{ mb: 1.5 }}>
        Card payments
      </Typography>
      <Box sx={{ mb: 5 }}>
        <StripeConnectionCard shopId={shopId} onNotice={setNotice} />
      </Box>

      <Typography variant="h6" component="h2" fontWeight={700} sx={{ mb: 1.5 }}>
        Parts suppliers
      </Typography>
      <Box sx={{ mb: 5 }}>
        <SupplierConnections shopId={shopId} onNotice={setNotice} />
      </Box>

      <Typography variant="h6" component="h2" fontWeight={700} sx={{ mb: 1.5 }}>
        Webhooks
      </Typography>
      {!webhooksAllowed && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Webhooks are turned off for this shop by the platform administrator
          {webhooksAccess?.reason ? ` (${webhooksAccess.reason})` : ''}. Nothing is sent to these
          endpoints until they are turned back on; the endpoints themselves are kept.
        </Alert>
      )}
      <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', mb: 5 }}>
        {loading ? (
          <Table>
            <TableBody>
              <TableSkeleton rows={3} columns={4} />
            </TableBody>
          </Table>
        ) : endpoints.length === 0 ? (
          <EmptyState
            icon={<HubOutlinedIcon />}
            title="No webhooks yet"
            description="Point a webhook at your accounting tool, a Zapier catch hook, or your own service to receive events as they happen."
            action={
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                New webhook
              </Button>
            }
          />
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Endpoint</TableCell>
                  <TableCell>Events</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {endpoints.map((endpoint) => (
                  <TableRow key={endpoint.id} hover>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Typography fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                        {endpoint.url}
                      </Typography>
                      {endpoint.description && (
                        <Typography variant="body2" color="text.secondary">
                          {endpoint.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={endpoint.events.includes(ALL) ? '' : endpoint.events.join(', ')}
                      >
                        <Chip size="small" variant="outlined" label={describeEvents(endpoint.events)} />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        status={endpoint.isActive ? 'active' : 'disconnected'}
                        label={endpoint.isActive ? 'Active' : 'Disabled'}
                      />
                      {endpoint.disabledReason && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {endpoint.disabledReason}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Send test event">
                          <span>
                            <IconButton
                              size="small"
                              disabled={!endpoint.isActive}
                              onClick={() => handleTest(endpoint)}
                            >
                              <SendOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delivery log">
                          <IconButton size="small" onClick={() => openLog(endpoint)}>
                            <ListAltOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Rotate signing secret">
                          <IconButton size="small" onClick={() => handleRotate(endpoint)}>
                            <KeyOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(endpoint)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setPendingDelete(endpoint)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Typography variant="h6" component="h2" fontWeight={700} sx={{ mb: 1.5 }}>
        Connected providers
      </Typography>
      <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {loading ? (
          <Table>
            <TableBody>
              <TableSkeleton rows={2} columns={3} />
            </TableBody>
          </Table>
        ) : connections.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Typography color="text.secondary">
              No providers connected. Square payments are managed from Settings; accounting,
              messaging and parts-supplier connections will appear here as they become available.
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Provider</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Connected</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {connections.map((connection) => (
                  <TableRow key={connection.id} hover>
                    <TableCell>
                      <Typography fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                        {connection.provider}
                      </Typography>
                      {connection.externalAccountId && (
                        <Typography variant="body2" color="text.secondary">
                          {connection.externalAccountId}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={connection.status} />
                      {connection.lastError && connection.status === 'error' && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {connection.lastError}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatWhen(connection.connectedAt)}</TableCell>
                    <TableCell align="right">
                      {connection.status !== 'disconnected' && (
                        <Button size="small" color="error" onClick={() => handleDisconnect(connection)}>
                          Disconnect
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit webhook' : 'New webhook'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Endpoint URL"
              required
              fullWidth
              value={form.url}
              onChange={(event) => setForm((current) => ({ ...current, url: event.target.value }))}
              helperText="Must be a public https address. Redirects are not followed."
            />
            <TextField
              label="Description"
              fullWidth
              value={form.description}
              onChange={(event) =>
                setForm((current) => ({ ...current, description: event.target.value }))
              }
              helperText="For your own reference — e.g. “QuickBooks via Zapier”."
            />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Events
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox checked={form.events.includes(ALL)} onChange={() => toggleEvent(ALL)} />
                  }
                  label="All events, including ones added later"
                />
                {!form.events.includes(ALL) &&
                  eventTypes.map((event) => (
                    <FormControlLabel
                      key={event.type}
                      control={
                        <Checkbox
                          checked={form.events.includes(event.type)}
                          onChange={() => toggleEvent(event.type)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600} component="span">
                            {event.type}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {event.description}
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', mb: 0.5, '& .MuiCheckbox-root': { pt: 0.5 } }}
                    />
                  ))}
              </FormGroup>
            </Box>
            {editing && (
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, isActive: event.target.checked }))
                    }
                  />
                }
                label="Active"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.url.trim() || form.events.length === 0}
          >
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Create webhook'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(revealedSecret)} onClose={() => setRevealedSecret(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Signing secret</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy this now — it will not be shown again. If you lose it, rotate the secret.
          </Alert>
          <TextField
            fullWidth
            value={revealedSecret ?? ''}
            InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }}
            onFocus={(event) => event.target.select()}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Each request carries an <code>X-Mechanix-Signature: t=…,v1=…</code> header: an
            HMAC-SHA256 of <code>{'{t}.{raw body}'}</code> with this secret. Reject requests whose
            signature does not match or whose <code>t</code> is more than five minutes old, and use
            the event <code>id</code> to ignore duplicates.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={copySecret}>Copy</Button>
          <Button variant="contained" onClick={() => setRevealedSecret(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(logFor)} onClose={() => setLogFor(null)} maxWidth="md" fullWidth>
        <DialogTitle>Delivery log</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, wordBreak: 'break-all' }}>
            {logFor?.url}
          </Typography>
          {logLoading ? (
            <Table>
            <TableBody>
              <TableSkeleton rows={4} columns={5} />
            </TableBody>
          </Table>
          ) : deliveries.length === 0 ? (
            <Typography color="text.secondary">Nothing sent to this endpoint yet.</Typography>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Event</TableCell>
                    <TableCell>Queued</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Attempts</TableCell>
                    <TableCell>Last response</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deliveries.map((delivery) => (
                    <TableRow key={delivery.id}>
                      <TableCell>{delivery.event?.type ?? '—'}</TableCell>
                      <TableCell>{formatWhen(delivery.createdAt)}</TableCell>
                      <TableCell>
                        <StatusChip status={delivery.status} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {delivery.attempts}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {delivery.lastError ??
                            (delivery.lastStatusCode ? `HTTP ${delivery.lastStatusCode}` : '—')}
                        </Typography>
                        {delivery.status === 'pending' && delivery.attempts > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            Next try {formatWhen(delivery.nextAttemptAt)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {delivery.status === 'failed' && (
                          <Button size="small" onClick={() => handleRetry(delivery)}>
                            Retry
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => logFor && loadDeliveries(logFor)}>Refresh</Button>
          <Button variant="contained" onClick={() => setLogFor(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(pendingDelete)} onClose={() => setPendingDelete(null)}>
        <DialogTitle>Remove this webhook?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Events still waiting to be delivered to it are dropped. Turn it off instead if you only
            need to pause it.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setPendingDelete(null)}>Keep it</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        message={notice}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <Box sx={{ height: 24 }} />
    </Container>
  );
};

export default IntegrationsPage;
